import { environment } from '../../../environments/environment';

/**
 * Reporte técnico de un error inesperado. Solo contexto técnico no sensible:
 * nunca tokens, contraseñas, payloads de formularios, DNI/CUIL ni responses crudas.
 */
export interface ErrorReport {
  appVersion: string;
  /** Ruta activa sin query params (los filtros pueden llevar datos personales). */
  route: string;
  /** Operación que falló: "MÉTODO /path" para HTTP o "unhandled" para errores globales. */
  operation: string;
  /** Nombre del tipo de error: "ApiError", "TypeError", "unknown", etc. */
  errorType: string;
  /** Mensaje ya pasado por sanitizeReportText. */
  message: string;
  online: boolean;
  /** Código de error normalizado del backend, si aplica. */
  code?: string;
  /** Correlation ID del backend para cruzar con sus logs, si aplica. */
  traceId?: string;
}

export interface ErrorReportContext {
  appVersion: string;
  route: string;
  online: boolean;
}

const MAX_MESSAGE_LENGTH = 300;

/**
 * Redacta datos sensibles de un texto antes de reportarlo: query strings de URLs
 * (Angular incluye la URL completa en sus mensajes de HttpErrorResponse), tokens
 * bearer, strings tipo JWT, emails, documentos con formato argentino (30.112.030,
 * 20-30112030-5) y corridas de 7+ dígitos (DNI, CUIL, montos, teléfonos). Además
 * trunca mensajes muy largos para no arrastrar payloads enteros.
 */
export function sanitizeReportText(text: string): string {
  const sanitized = text
    .replace(/((?:https?:\/\/|\/)[\w\-.:~%/]*)\?\S+/g, '$1?[redactado]')
    .replace(/Bearer\s+[\w\-.~+/]+=*/gi, '[redactado]')
    .replace(/\b[\w-]{8,}\.[\w-]{8,}\.[\w-]{4,}\b/g, '[redactado]')
    .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, '[redactado]')
    .replace(/\b\d{1,3}(?:[.-]\d{2,3}){2,}\b/g, '[redactado]')
    .replace(/\d{7,}/g, '[redactado]');
  return sanitized.length > MAX_MESSAGE_LENGTH ? `${sanitized.slice(0, MAX_MESSAGE_LENGTH)}…` : sanitized;
}

/** Contexto actual del navegador para armar un reporte. */
export function reportContext(route: string): ErrorReportContext {
  return {
    appVersion: environment.appVersion,
    route,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
  };
}

/** Construye el reporte a partir de cualquier valor lanzado, sin serializar desconocidos. */
export function buildErrorReport(operation: string, error: unknown, context: ErrorReportContext): ErrorReport {
  const { errorType, message, code, traceId } = describeError(error);
  return {
    appVersion: context.appVersion,
    route: context.route.split('?')[0],
    operation: sanitizeReportText(operation),
    errorType,
    message: sanitizeReportText(message),
    online: context.online,
    ...(code !== undefined ? { code } : {}),
    ...(traceId !== undefined ? { traceId } : {}),
  };
}

function describeError(error: unknown): { errorType: string; message: string; code?: string; traceId?: string } {
  if (isApiErrorLike(error)) {
    return { errorType: 'ApiError', message: error.message, code: error.code, traceId: error.traceId };
  }
  if (error instanceof Error) {
    return { errorType: error.name || 'Error', message: error.message };
  }
  // Un valor lanzado arbitrario podría contener payloads: no se serializa jamás.
  return { errorType: 'unknown', message: 'error no serializable' };
}

/** Forma estructural del ApiError normalizado por error.interceptor. */
function isApiErrorLike(value: unknown): value is { code: string; message: string; traceId: string } {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    typeof candidate['message'] === 'string' &&
    typeof candidate['traceId'] === 'string'
  );
}
